import api from '../../../api';
import { sendTaskEmailToUser, getTaskEmailTemplates, checkEmailSettings } from './taskEmailApi';

/**
 * Get all tasks from logged-in user's assigned leads
 * Supports permission-based filtering via viewOwnUserId
 */
export const getTasks = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add viewOwnUserId if provided (for view_own permission)
    if (params.viewOwnUserId) {
      queryParams.append('viewOwnUserId', params.viewOwnUserId);
    }
    
    // Add viewDepartmentId if provided (for view_department permission)
    if (params.viewDepartmentId) {
      queryParams.append('viewDepartmentId', params.viewDepartmentId);
    }
    
    // Add departmentId if provided (for user-selected department filter)
    if (params.departmentId) {
      const departmentParam = Array.isArray(params.departmentId)
        ? params.departmentId.filter(Boolean).join(',')
        : String(params.departmentId).trim();
      if (departmentParam) queryParams.append('departmentId', departmentParam);
    }

    // Add due date filters if provided
    if (params.dateFrom) {
      queryParams.append('dateFrom', params.dateFrom);
    }
    if (params.dateTo) {
      queryParams.append('dateTo', params.dateTo);
    }
    if (params.dueDateFrom) {
      queryParams.append('dueDateFrom', params.dueDateFrom);
    }
    if (params.dueDateTo) {
      queryParams.append('dueDateTo', params.dueDateTo);
    }

    // Status: multi-select (array) or comma-separated string for backend
    if (params.status != null && params.status !== '') {
      const statusParam = Array.isArray(params.status)
        ? params.status.filter(Boolean).join(',')
        : String(params.status).trim();
      if (statusParam) queryParams.append('status', statusParam);
    }
    // Assigned user filter (e.g. from Dashboard "Companies Contacted per Team Member" redirect)
    if (params.assignedUser) {
      const assignedUserParam = Array.isArray(params.assignedUser)
        ? params.assignedUser.filter(Boolean).join(',')
        : String(params.assignedUser).trim();
      if (assignedUserParam) queryParams.append('assignedUserId', assignedUserParam);
    }

    if (params.branchId) {
      const branchParam = Array.isArray(params.branchId)
        ? params.branchId.filter(Boolean).join(',')
        : String(params.branchId).trim();
      if (branchParam) queryParams.append('branchId', branchParam);
    }

    // Pagination (backend uses Spring Pageable: page 0-based, size)
    if (params.page != null && params.page >= 0) {
      queryParams.append('page', params.page);
    }
    if (params.size != null && params.size > 0) {
      queryParams.append('size', params.size);
    }

    if (params.search != null && String(params.search).trim() !== '') {
      queryParams.append('search', String(params.search).trim());
    }

    // Spring Data: sort=fieldName,direction (e.g. clientName,asc)
    if (params.sort != null && String(params.sort).trim() !== '') {
      queryParams.append('sort', String(params.sort).trim());
    }

    const url = queryParams.toString() ? `/tasks?${queryParams}` : '/tasks';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }
};

/**
 * Get project-based audit tasks
 */
export const getAuditTasks = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.projectId) queryParams.append('projectId', params.projectId);
    if (params.status) queryParams.append('status', params.status);

    const url = queryParams.toString() ? `/audit/tasks?${queryParams}` : '/audit/tasks';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching audit tasks:', error);
    throw error;
  }
};

/**
 * Get active internal users eligible for audit task reassignment (privileged only)
 */
export const getAuditAssignees = async (taskTypeCode) => {
  try {
    const params = taskTypeCode ? `?taskTypeCode=${encodeURIComponent(taskTypeCode)}` : '';
    const response = await api.get(`/audit/assignees${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching audit assignees:', error);
    throw error;
  }
};

/**
 * Update audit task status (DONE or OPEN)
 */
export const updateAuditTaskStatus = async (taskId, status) => {
  try {
    const response = await api.patch(`/audit/tasks/${taskId}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating audit task status:', error);
    throw error;
  }
};

/**
 * Reassign audit task to a different user (privileged only)
 */
export const reassignAuditTask = async (taskId, assignedUserId, notes) => {
  try {
    const response = await api.patch(`/audit/tasks/${taskId}/assignee`, {
      task_id: taskId,
      new_auditor_id: assignedUserId,
      assignedUserId,
      notes,
    });
    return response.data;
  } catch (error) {
    console.error('Error reassigning audit task:', error);
    throw error;
  }
};

/**
 * Get task by ID
 */
export const getTaskById = async (id) => {
  try {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching task:', error);
    throw error;
  }
};

/**
 * Get user's assigned leads for dropdowns
 */
export const getUserLeads = async () => {
  try {
    const response = await api.get('/tasks/user-leads');
    return response.data;
  } catch (error) {
    console.error('Error fetching user leads:', error);
    throw error;
  }
};

/**
 * Get unique clients from user's assigned leads
 */
export const getUserClients = async () => {
  try {
    const response = await api.get('/tasks/user-clients');
    return response.data;
  } catch (error) {
    console.error('Error fetching user clients:', error);
    throw error;
  }
};

/**
 * Get projects by client ID
 */
export const getUserProjectsByClient = async (clientId) => {
  try {
    const response = await api.get(`/tasks/user-projects/${clientId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

/**
 * Create task using LeadTaskController (with multipart/form-data)
 * Includes email notification to assigned user
 */
export const createTask = async (taskData) => {
  try {
    const formData = new FormData();
    
    // Prepare task request object
    const taskRequest = {
      title: taskData.taskTitle,
      type: taskData.type,
      priority: taskData.priority,
      status: taskData.status,
      description: taskData.description,
      dueDate: taskData.dueDate ? new Date(taskData.dueDate).toISOString() : null,
      leadId: taskData.leadId || taskData.projectName, // Optional - for lead-specific tasks
      companyId: taskData.clientName ? parseInt(taskData.clientName) : null, // NEW - company level tasks
      contactId: taskData.contactName ? parseInt(taskData.contactName) : null, // NEW - contact person
      assignedUserId: taskData.assignedUserId
    };
    
    // Add JSON data as "data" part
    formData.append('data', new Blob([JSON.stringify(taskRequest)], {
      type: 'application/json'
    }));
    
    // Add files if any
    if (taskData.documents && taskData.documents.length > 0) {
      // For now, use the first document (backend supports single file)
      const file = taskData.documents[0].file;
      if (file) {
        formData.append('file', file);
      }
    }
    
    const response = await api.post('/crm/lead-tasks', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    // ✅ SEND EMAIL NOTIFICATION TO ASSIGNED USER (fire-and-forget to avoid blocking UI)
    const createdTask = response.data;
    if (taskData.assignedUserId) {
      (async () => {
        try {
          console.log('=== Sending email notification for new task (async) ===');
          
          const [emailSettings, emailTemplates] = await Promise.all([
            checkEmailSettings(),
            getTaskEmailTemplates()
          ]);
          
          let assignedUserEmail = null;
          try {
            const userResponse = await api.get(`/md/internal-users/${taskData.assignedUserId}`);
            assignedUserEmail = userResponse.data?.email;
          } catch (userError) {
            console.warn('⚠️ Failed to get assigned user email:', userError);
          }
          
          if (emailSettings && assignedUserEmail) {
            const companyId = taskData.clientName ? parseInt(taskData.clientName, 10) : null;
            const appBaseUrl = window.location.origin;
            const taskLink = companyId ? `${appBaseUrl}/clients/${companyId}` : '';

            const emailTaskData = {
              id: createdTask.id,
              taskTitle: taskData.taskTitle,
              type: taskData.type,
              priority: taskData.priority,
              status: taskData.status,
              description: taskData.description,
              dueDate: taskData.dueDate ? new Date(taskData.dueDate).toLocaleDateString() : 'N/A',
              clientName: taskData.clientNameDisplay || taskData.clientName || 'N/A',
              projectName: taskData.projectNameDisplay || taskData.projectName || 'N/A',
              contactName: taskData.contactNameDisplay || taskData.contactName || 'N/A',
              assignedUser: taskData.assignedUserDisplay || 'User',
              createdBy: taskData.createdBy || 'System',
              createdAt: new Date().toLocaleDateString(),
              companyId: companyId != null ? String(companyId) : '',
              taskLink,
              task_link: taskLink
            };
            
            await sendTaskEmailToUser(assignedUserEmail, emailSettings, emailTemplates, emailTaskData);
            console.log('✅ Email notification sent successfully (async)');
          } else {
            console.warn('⚠️ Cannot send email - missing settings or user email');
            console.warn('Email Settings:', emailSettings ? 'Available' : 'Not configured');
            console.warn('Assigned To Email:', assignedUserEmail || 'Not found');
          }
        } catch (emailError) {
          console.error('❌ Failed to send email notification (async):', emailError);
        }
      })();
    }
    
    return response.data;
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

/**
 * Update task using LeadTaskController (with multipart/form-data)
 */
export const updateTask = async (id, taskData) => {
  try {
    const formData = new FormData();
    
    // Prepare task request object
    const taskRequest = {
      title: taskData.taskTitle,
      type: taskData.type,
      priority: taskData.priority,
      status: taskData.status,
      description: taskData.description,
      dueDate: taskData.dueDate ? new Date(taskData.dueDate).toISOString() : null,
      leadId: taskData.leadId || taskData.projectName, // Optional - for lead-specific tasks
      companyId: taskData.clientName ? parseInt(taskData.clientName) : null, // NEW - company level tasks
      contactId: taskData.contactName ? parseInt(taskData.contactName) : null, // NEW - contact person
      assignedUserId: taskData.assignedUserId
    };
    
    // Add JSON data as "data" part
    formData.append('data', new Blob([JSON.stringify(taskRequest)], {
      type: 'application/json'
    }));
    
    // Add files if any
    if (taskData.documents && taskData.documents.length > 0) {
      const file = taskData.documents[0].file;
      if (file) {
        formData.append('file', file);
      }
    }
    
    const response = await api.put(`/crm/lead-tasks/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};

/**
 * Delete task
 */
export const deleteTask = async (id) => {
  try {
    const response = await api.delete(`/tasks/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};

/**
 * Get assigned leads - leads assigned to current user but created by someone else
 * This is specifically for the Task Management assigned leads tab
 */
export const getAssignedLeads = async () => {
  try {
    const response = await api.get('/crm/leads/assigned-leads');
    return response.data;
  } catch (error) {
    console.error('Error fetching assigned leads:', error);
    throw error;
  }
};

/**
 * Create (or ensure) a Technical Review task for a given project
 */
export const createAuditTask = async (projectId, scope = {}) => {
  const response = await api.post(`/audit/projects/${projectId}/tasks/audit`, null, { params: scope });
  return response.data;
};

export const createTechnicalReviewTask = async (projectId, scope = {}) => {
  try {
    const response = await api.post(`/audit/projects/${projectId}/tasks/technical-review`, null, { params: scope });
    return response.data;
  } catch (error) {
    console.error('Error creating technical review task:', error);
    throw error;
  }
};

export const createCertificateIssuanceTask = async (projectId, scope = {}) => {
  const response = await api.post(`/audit/projects/${projectId}/tasks/certificate-issuance`, null, { params: scope });
  return response.data;
};

export const getCertificate = async (projectId) => {
  const response = await api.get(`/audit/projects/${projectId}/certificate`);
  return response.data;
};

export const issueCertificate = async (projectId, data) => {
  const response = await api.post(`/audit/projects/${projectId}/certificate/issue`, data);
  return response.data;
};
